'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import HavasLogo from '../assets/HavasLogo';
import CTAPrimaryButton from './CTAPrimaryButton';
import { useStaggeredAnimateIn } from '@/lib/useAnimateIn';
import { trackEvent } from '@/lib/analytics';
import { useContactModal } from '@/contexts/ContactModalContext';
// removed ThemeToggle

const navigationItems = [
  { name: 'What we do', href: '/what-we-do' },
  { 
    name: 'Our work', 
    href: '/work',
    dropdown: [
      { name: 'American Airlines', href: '/case-studies/american-airlines' },
      { name: 'Brooklyn Film Festival', href: '/case-studies/brooklyn-film-festival' },
      { name: 'Google Fitbit', href: '/case-studies/google-fitbit' },
      { name: 'The House of Suntory', href: '/case-studies/house-of-suntory' },
      { name: 'Staybl', href: '/case-studies/staybl' }
    ]
  },
  { name: 'Who we are', href: '/about' },
  { name: 'Careers', href: '/careers' }
];

export default function Navigation() {
  const pathname = usePathname();
  
  // Helper function to track navigation clicks
  const handleNavClick = (clickText: string, clickUrl: string) => {
    // console.log('[Navigation] Tracking nav_click:', { clickText, clickUrl });
    trackEvent('nav_click', {
      click_text: clickText,
      click_url: clickUrl,
      page_path: pathname
    });
  };
  
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const { openModal: openContactModal } = useContactModal();
  const [isAiContactModalOpen, setIsAiContactModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);
  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false);
  const [isCaseStudyPage, setIsCaseStudyPage] = useState(false);
  const [shouldUseWhiteText, setShouldUseWhiteText] = useState(false);
  const [navItemsVisible, setNavItemsVisible] = useState(false);
  const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
  const isFirstLoadRef = useRef(true);
  const [isSubsequentLoad, setIsSubsequentLoad] = useState(false);
  const prevPathnameRef = useRef(pathname);
  const hasAutoOpenedDropdownRef = useRef(false);
  const [mobileMenuTopPadding, setMobileMenuTopPadding] = useState(200);
  const mobileMenuItemsRef = useRef<HTMLDivElement>(null);

  // Staggered animation for mobile menu items
  const { getItemStyle: getMobileItemStyle, trigger: triggerMobileItems, reset: resetMobileItems } = useStaggeredAnimateIn({
    delay: 200,
    staggerDelay: 80,
    autoStart: false
  });

  // Staggered animation for dropdown items
  const { getItemStyle: getDropdownItemStyle, trigger: triggerDropdownItems, reset: resetDropdownItems } = useStaggeredAnimateIn({
    delay: 0,
    staggerDelay: 60,
    autoStart: false
  });

  // Check if we're on a case study page
  useEffect(() => {
    setIsCaseStudyPage(pathname.startsWith('/case-studies/'));
    
    // Set initial text color for case study pages
    if (pathname.startsWith('/case-studies/')) {
      const container = document.querySelector('[data-nav-initial-text]') as HTMLElement | null;
      const initial = container?.getAttribute('data-nav-initial-text');
      const lock = container?.getAttribute('data-nav-text-lock');
      if (lock === 'black') {
        setShouldUseWhiteText(false);
        return;
      }
      if (lock === 'white') {
        setShouldUseWhiteText(true);
        return;
      }
      if (initial === 'black') {
        setShouldUseWhiteText(false);
      } else if (initial === 'white') {
        setShouldUseWhiteText(true);
      } else {
        setShouldUseWhiteText(true);
      }
    }
  }, [pathname]);

  // Close dropdown if we navigate away from case study pages (only when menu is open)
  useEffect(() => {
    if (isOpen && !pathname.startsWith('/case-studies/')) {
      setActiveMobileDropdown(null);
      hasAutoOpenedDropdownRef.current = false;
    }
  }, [pathname, isOpen]);

  // Track pathname changes to detect subsequent loads - useLayoutEffect to prevent flash
  useLayoutEffect(() => {
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      prevPathnameRef.current = pathname;
      // First load: trigger animation after 1.5 seconds
      const timer = setTimeout(() => {
        setNavItemsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else if (pathname !== prevPathnameRef.current) {
      // Subsequent load: reset immediately to prevent flash
      prevPathnameRef.current = pathname;
      setIsSubsequentLoad(true);
      // Set to false immediately (synchronously) before paint
      setNavItemsVisible(false);
      
      // Wait for page transition to complete (0.4s) + small buffer = 0.4s
      const timer = setTimeout(() => {
        setNavItemsVisible(true);
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [pathname]);

  // Calculate dynamic top padding for mobile menu items to center them (only on menu open)
  useEffect(() => {
    if (isOpen && mobileMenuItemsRef.current) {
      // Calculate after a delay to ensure content is rendered, including any auto-opened accordions
      // Use a longer delay to account for accordion auto-opening on case study pages (150ms) + animation time
      const isCaseStudy = pathname.startsWith('/case-studies/');
      const delay = isCaseStudy ? 400 : 200; // Longer delay if accordion will auto-open
      
      const timer = setTimeout(() => {
        const viewportHeight = window.innerHeight;
        const itemsHeight = mobileMenuItemsRef.current?.scrollHeight || 0;
        // Calculate padding to center: (viewport height - items height) / 2
        // Add some minimum padding and account for bottom padding (pb-20 = 5rem = 80px)
        // Subtract 7.5rem (120px) to move nav items up even more
        const minPaddingPx = 20; // 1.25rem
        const bottomPaddingPx = 80; // 5rem (pb-20 Tailwind)
        const offsetPx = 120; // 7.5rem
        const calculatedPadding = Math.max(minPaddingPx, (viewportHeight - itemsHeight - bottomPaddingPx) / 2 - offsetPx);
        setMobileMenuTopPadding(calculatedPadding);
      }, delay);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen, pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore body scroll when menu closes
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // Handle mobile panel animations
  useEffect(() => {
    if (isOpen) {
      // Panel slides down from top
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMobilePanelVisible(true);
          // Trigger staggered animations for items after panel animation starts
          setTimeout(() => {
            triggerMobileItems();
          }, 100);
        });
      });
      
      // Auto-open "Our Work" dropdown if on a case study page (only once when menu first opens)
      const isMobile = window.innerWidth < 1024;
      if (isMobile && pathname.startsWith('/case-studies/') && !hasAutoOpenedDropdownRef.current) {
        // Use a small delay to ensure the menu is fully open before opening dropdown
        setTimeout(() => {
          setActiveMobileDropdown('Our work');
          hasAutoOpenedDropdownRef.current = true;
        }, 150);
      }
    } else {
      setMobilePanelVisible(false);
      resetMobileItems();
      resetDropdownItems();
      // Close the dropdown when menu closes and reset the auto-open flag
      setActiveMobileDropdown(null);
      hasAutoOpenedDropdownRef.current = false;
    }
  }, [isOpen, pathname, triggerMobileItems, resetMobileItems, resetDropdownItems]);

  // Trigger dropdown animation when a dropdown opens
  useEffect(() => {
    if (activeMobileDropdown) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          triggerDropdownItems();
        });
      });
    } else {
      resetDropdownItems();
    }
  }, [activeMobileDropdown, triggerDropdownItems, resetDropdownItems]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      const container = document.querySelector('[data-nav-text-lock]') as HTMLElement | null;
      const lock = container?.getAttribute('data-nav-text-lock');
      const lastScrollY = lastScrollYRef.current;
      
      // Check if scrolled past 3.125rem (50px) - only update if value changed
      const newThresholdValue = currentScrollY > 50;
      if (newThresholdValue !== hasScrolledPastThreshold) {
        setHasScrolledPastThreshold(newThresholdValue);
      }
      
      // For case study pages, determine text color based on scroll position
      if (isCaseStudyPage && !lock) {
        // Use white text when in the hero section (first 40vh), black when scrolled past
        // Changed from 80vh to 40vh to match when background color transition starts
        setShouldUseWhiteText(currentScrollY < viewportHeight * 0.05);
      }
      
      // Apply scroll hide/show behavior - but keep mobile nav visible when menu is open
      // Never show nav when AI contact modal is open
      if ((!isMobile || !isOpen) && !isAiContactModalOpen) {
        // Show nav when scrolling up or at the top
        if (currentScrollY < lastScrollY || currentScrollY < 10) {
          setIsVisible(true);
        } 
        // Hide nav when scrolling down (but not at the very top)
        else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsVisible(false);
        }
      }
      
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isCaseStudyPage, isOpen, isAiContactModalOpen, hasScrolledPastThreshold]);

  // Listen for AI contact modal opening to hide navigation
  useEffect(() => {
    const handleContactModalOpen = () => {
      setIsAiContactModalOpen(true);
      setIsVisible(false);
    };

    const handleContactModalClose = () => {
      setIsAiContactModalOpen(false);
      // Don't automatically show nav - let scroll behavior handle it
    };

    window.addEventListener('contactModalOpen', handleContactModalOpen);
    window.addEventListener('contactModalClose', handleContactModalClose);
    
    return () => {
      window.removeEventListener('contactModalOpen', handleContactModalOpen);
      window.removeEventListener('contactModalClose', handleContactModalClose);
    };
  }, []);

  return (
    <>
      {/* // Desktop */}
      <nav 
        aria-label="Main navigation"
        className={`hidden lg:block fixed left-0 pt-6 pb-6 right-0 z-50 ${hasScrolledPastThreshold ? 'lg:bg-eggshell' : ''} transition-all ${
          isVisible ? 'duration-500 ease-out' : 'duration-300 ease-in'
        } ${
          isVisible ? 'translate-y-0' : '-translate-y-[calc(100%+1.5rem)]'
        }`}
      >
        <div className="max-w-[120rem] mx-auto px-[2.125rem]">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link 
              href="/" 
              onClick={() => handleNavClick('Logo', '/')}
              aria-label="Havas CX - Go to homepage"
              className={`flex items-center w-[6.25rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                navItemsVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-[1.25rem] opacity-0'
              } ${
                isCaseStudyPage && shouldUseWhiteText ? 'text-white' : 'text-black'
              }`}
              style={{
                transitionProperty: navItemsVisible ? 'transform, opacity' : 'none',
                transitionDuration: navItemsVisible ? '500ms' : '0ms',
                transitionTimingFunction: 'ease-out',
                transitionDelay: navItemsVisible ? '0ms' : '0ms'
              }}
            >
              <HavasLogo aria-hidden="true" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 mt-[0.25rem]">
              {navigationItems.map((item, index) => {
                const hasDropdown = 'dropdown' in item && item.dropdown;
                // Check if current page is active
                // For case study pages, mark "Featured work" as active
                const isActive = pathname === item.href || 
                  (item.href === '/work' && pathname.startsWith('/case-studies/'));
                
                return (
                  <div 
                    key={item.name}
                    className={`relative ${hasDropdown && activeDropdown === item.name ? 'z-[100]' : ''} ${
                      navItemsVisible 
                        ? 'translate-y-0 opacity-100' 
                        : 'translate-y-[1.25rem] opacity-0'
                    }`}
                    style={{
                      transitionProperty: navItemsVisible ? 'transform, opacity' : 'none',
                      transitionDuration: navItemsVisible ? '500ms' : '0ms',
                      transitionTimingFunction: 'ease-out',
                      transitionDelay: navItemsVisible ? `${index * 100}ms` : '0ms'
                    }}
                    onMouseEnter={() => hasDropdown && setActiveDropdown(item.name)}
                    onMouseLeave={() => hasDropdown && setActiveDropdown(null)}
                  >
                    {hasDropdown ? (
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setActiveDropdown(activeDropdown === item.name ? null : item.name);
                          }
                        }}
                        aria-expanded={activeDropdown === item.name}
                        aria-haspopup="true"
                        aria-label={`${item.name} menu`}
                        className={`mx-3 text-base flex items-center gap-1 relative transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 pb-1 cursor-pointer ${
                          isCaseStudyPage && shouldUseWhiteText ? 'text-white' : 'text-black'
                        } ${isActive ? `after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:w-full ${isCaseStudyPage && shouldUseWhiteText ? 'after:bg-white' : 'after:bg-black'}` : ''} ${
                          !isActive && item.name !== 'Our work' ? `after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:w-0 after:transition-all after:duration-300 after:ease-out hover:after:w-full ${
                            isCaseStudyPage && shouldUseWhiteText ? 'after:bg-[#FDFEF6]' : 'after:bg-black'
                          }` : ''
                        }`}>
                        {item.name}
                        <svg 
                          width="12" 
                          height="8" 
                          viewBox="0 0 12 8" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                          className={`transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        >
                          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        onClick={() => handleNavClick(item.name, item.href)}
                        className={`mx-3 text-base flex items-center gap-1 relative transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 pb-1 cursor-pointer ${
                          isCaseStudyPage && shouldUseWhiteText ? 'text-white' : 'text-black'
                        } ${isActive ? 'after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:w-full after:bg-black' : ''} ${
                          !isActive ? `after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:w-0 after:transition-all after:duration-300 after:ease-out hover:after:w-full ${
                            isCaseStudyPage && shouldUseWhiteText ? 'after:bg-[#FDFEF6]' : 'after:bg-black'
                          }` : ''
                        }`}>
                        {item.name}
                      </Link>
                    )}
                    {hasDropdown && activeDropdown === item.name && (
                      <div 
                        key={`dropdown-${activeDropdown}`}
                        role="menu"
                        aria-label={`${item.name} submenu`}
                        className="absolute top-full left-0 -mt-1 bg-[#FDFEF6] border border-gray-200 rounded-b-lg shadow-lg w-fit py-2 z-[100]"
                        style={{
                          animation: 'fadeInSlideDown 0.3s ease-out forwards',
                          opacity: 0
                        }}
                      >
                        {item.dropdown.map((dropdownItem) => {
                          const isDropdownActive = pathname === dropdownItem.href;
                          return (
                            <Link
                              key={dropdownItem.name}
                              href={dropdownItem.href}
                              onClick={() => {
                                handleNavClick(dropdownItem.name, dropdownItem.href);
                                setActiveDropdown(null);
                              }}
                              role="menuitem"
                              className={`block px-4 py-2 text-base transition-all duration-200 whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                                isDropdownActive 
                                  ? '' 
                                  : ''
                              }`}
                            >
                              <span className={`inline-block pb-1 relative ${
                                isDropdownActive 
                                  ? 'after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:w-full after:bg-black' 
                                  : 'after:content-[""] after:absolute after:bottom-[0.0625rem] after:left-0 after:h-[0.0625rem] after:bg-black after:w-0 after:transition-all after:duration-300 after:ease-out hover:after:w-full'
                              }`}>
                                {dropdownItem.name}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              
            </div>

            <div
              className={`${
                navItemsVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-[1.25rem] opacity-0'
              }`}
              style={{
                transitionProperty: navItemsVisible ? 'transform, opacity' : 'none',
                transitionDuration: navItemsVisible ? '500ms' : '0ms',
                transitionTimingFunction: 'ease-out',
                transitionDelay: navItemsVisible ? `${navigationItems.length * 100}ms` : '0ms'
              }}
            >
              <CTAPrimaryButton 
                onClick={() => {
                  trackEvent('nav_click', {
                    click_text: 'Get in touch',
                    click_url: 'Get_in_touch',
                    page_path: pathname
                  });
                  openContactModal();
                }}
                showArrow={true}
                useWhiteText={isCaseStudyPage && shouldUseWhiteText}
              >
                Get in touch
              </CTAPrimaryButton>
            </div>
          </div>
        </div>
      </nav>
    
      {/* Mobile Navigation */}
      <nav 
        aria-label="Main navigation"
        className={`${hasScrolledPastThreshold ? 'bg-eggshell' : ''} lg:hidden fixed pt-6 pb-4 left-0 right-0 z-[10000] transition-all ${
          isVisible ? 'duration-500 ease-out' : 'duration-300 ease-in'
        } ${
          isVisible ? 'translate-y-0' : '-translate-y-[calc(100%+1.5rem)]'
        } ${isOpen ? 'bg-eggshell' : ''}`}
      >
        <div className="max-w-8xl px-4">
          <div className="flex justify-between items-center relative z-[60]">
            {/* Logo */}
            <Link 
              href="/" 
              onClick={() => {
                handleNavClick('Logo', '/');
                if (isOpen) {
                  setIsOpen(false);
                }
              }}
              aria-label="Havas CX - Go to homepage"
              className={`flex items-center w-[6.25rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                navItemsVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-[1.25rem] opacity-0'
              } ${
                isOpen ? 'text-black' : (isCaseStudyPage && shouldUseWhiteText ? 'text-white' : 'text-black')
              }`}
              style={{
                transitionProperty: navItemsVisible ? 'transform, opacity' : 'none',
                transitionDuration: navItemsVisible ? '500ms' : '0ms',
                transitionTimingFunction: 'ease-out',
                transitionDelay: navItemsVisible ? '0ms' : '0ms'
              }}
            >
              <HavasLogo aria-hidden="true" />
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsOpen(!isOpen);
                }
              }}
              aria-expanded={isOpen}
              aria-controls="mobile-menu-panel"
              aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className={`focus-visible:outline-none relative z-[60] focus-visible:ring-2 focus-visible:ring-blue-500 rounded ${
                navItemsVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-[1.25rem] opacity-0'
              } ${
                isOpen ? 'text-black' : (isCaseStudyPage && shouldUseWhiteText ? 'text-white' : 'text-black')
              }`}
              style={{
                transitionProperty: navItemsVisible ? 'transform, opacity' : 'none',
                transitionDuration: navItemsVisible ? '500ms' : '0ms',
                transitionTimingFunction: 'ease-out',
                transitionDelay: navItemsVisible ? '100ms' : '0ms'
              }}
            >
              <svg
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                {isOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <nav 
        id="mobile-menu-panel"
        aria-label="Mobile navigation"
        className={`lg:hidden fixed inset-0 bg-eggshell z-[9998] ${
          isOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
        style={{
          transform: (isOpen && mobilePanelVisible) ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 400ms ease-out',
          opacity: isOpen ? 1 : 0,
          touchAction: 'none'
        }}
        onTouchMove={(e) => {
          // Prevent touch scrolling on the overlay itself (only allow scrolling in the container)
          if (e.target === e.currentTarget) {
            e.preventDefault();
          }
        }}
        onWheel={(e) => {
          // Prevent wheel events from reaching body
          e.stopPropagation();
        }}
      >
        <div 
          className="scrolling-nav-menu-container h-full overflow-y-auto pt-24"
          style={{ touchAction: 'pan-y' }}
          onWheel={(e) => {
            // Prevent wheel events from bubbling to body
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Allow touch scrolling within the container, but prevent bubbling
            e.stopPropagation();
          }}
          onScroll={(e) => {
            // Prevent scroll events from bubbling to body
            e.stopPropagation();
          }}
        >
        <div className="mobile-menu-items-container flex flex-col min-h-full px-8 pb-20" style={{ paddingTop: `${mobileMenuTopPadding}px` }}>
          {/* Navigation Links */}
          <div ref={mobileMenuItemsRef} className="mobile-menu-items flex flex-col space-y-8 pb-10">
            {navigationItems.map((item, index) => {
              // Check if current page is active
              // For case study pages, mark "Featured work" as active
              const isActive = pathname === item.href || 
                (item.href === '/work' && pathname.startsWith('/case-studies/'));
              const hasDropdown = 'dropdown' in item && item.dropdown;
              return (
                <div 
                  key={item.name} 
                  className="flex flex-col space-y-4"
                  style={getMobileItemStyle(index)}
                >
                  {hasDropdown ? (
                    <button
                      onClick={() => setActiveMobileDropdown(
                        activeMobileDropdown === item.name ? null : item.name
                      )}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveMobileDropdown(
                            activeMobileDropdown === item.name ? null : item.name
                          );
                        }
                      }}
                      aria-expanded={activeMobileDropdown === item.name}
                      aria-haspopup="true"
                      aria-label={`${item.name} menu`}
                      className={`text-3xl transition-all duration-200 relative flex items-center gap-2 pb-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                        isActive
                          ? 'text-black'
                          : 'font-light'
                      }`}
                    >
                      <span className={`inline-block ${
                        isActive && item.name !== 'Our work'
                          ? `relative after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-[0.0625rem] after:w-full ${isCaseStudyPage && shouldUseWhiteText ? 'after:bg-white' : 'after:bg-black'}` 
                          : ''
                      }`}>
                        {item.name}
                      </span>
                      <svg 
                        width="16" 
                        height="10" 
                        viewBox="0 0 16 10" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className={`transition-transform duration-200 ${
                          activeMobileDropdown === item.name ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      >
                        <path d="M1 1L8 8L15 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ) : (
                    <Link
                      href={item.href}
                      className={`text-3xl transition-all duration-200 pb-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                        isActive
                          ? 'text-black'
                          : 'font-light'
                      }`}
                      onClick={() => {
                        handleNavClick(item.name, item.href);
                        setIsOpen(false);
                      }}
                    >
                      <span className={`inline-block relative ${
                        isActive
                          ? 'after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-[0.0625rem] after:w-full after:bg-black'
                          : ''
                      }`}>
                        {item.name}
                      </span>
                    </Link>
                  )}
                  {hasDropdown && activeMobileDropdown === item.name && (
                    <div 
                      role="menu"
                      aria-label={`${item.name} submenu`}
                      className="flex flex-col space-y-2 mt-4"
                    >
                      {item.dropdown.map((dropdownItem, dropdownIndex) => {
                        const isDropdownActive = pathname === dropdownItem.href;
                        return (
                          <Link
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            role="menuitem"
                            className={`text-xl font-light transition-all duration-200 pb-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:rounded ${
                              isDropdownActive
                                ? 'text-black'
                                : ''
                            }`}
                            onClick={() => {
                              handleNavClick(dropdownItem.name, dropdownItem.href);
                              setIsOpen(false);
                            }}
                            style={{ ...getDropdownItemStyle(dropdownIndex), transform: 'none' }}
                          >
                            <span className={`inline-block relative ${
                              isDropdownActive
                                ? 'after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-black'
                                : ''
                            }`}>
                              {dropdownItem.name}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Get in Touch Button */}
          <div className="flex mt-auto" style={getMobileItemStyle(navigationItems.length)}>
            <CTAPrimaryButton
              onClick={() => {
                trackEvent('nav_click', {
                  click_text: 'Get in touch',
                  click_url: 'Get_in_touch',
                  page_path: pathname
                });
                setIsOpen(false);
                openContactModal();
              }}
              showArrow={true}
              useWhiteText={false}
            >
              Get in touch
            </CTAPrimaryButton>
          </div>
          </div>
        </div>
      </nav>
    </>
  );
}

